import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Truck, Key, Save, RefreshCw, CheckCircle, Package, MapPin,
  Eye, EyeOff, ExternalLink, AlertCircle, Ban, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminShipping() {
  // Transdirect
  const [tdApiKey, setTdApiKey] = useState('');
  const [tdHasKey, setTdHasKey] = useState(false);
  const [tdShowKey, setTdShowKey] = useState(false);
  const [tdLoading, setTdLoading] = useState(true);
  const [tdSaving, setTdSaving] = useState(false);
  const [tdTesting, setTdTesting] = useState(false);
  const [tdTestResult, setTdTestResult] = useState(null);

  // AusPost
  const [apApiKey, setApApiKey] = useState('');
  const [apHasKey, setApHasKey] = useState(false);
  const [apShowKey, setApShowKey] = useState(false);
  const [apLoading, setApLoading] = useState(true);
  const [apSaving, setApSaving] = useState(false);
  const [apTesting, setApTesting] = useState(false);
  const [apTestResult, setApTestResult] = useState(null);

  // Interparcel
  const [ipApiKey, setIpApiKey] = useState('');
  const [ipHasKey, setIpHasKey] = useState(false);
  const [ipShowKey, setIpShowKey] = useState(false);
  const [ipLoading, setIpLoading] = useState(true);
  const [ipSaving, setIpSaving] = useState(false);
  const [ipTesting, setIpTesting] = useState(false);
  const [ipTestResult, setIpTestResult] = useState(null);

  // Provider toggles
  const [enabledProviders, setEnabledProviders] = useState(['transdirect', 'auspost']);

  // Courier management (Transdirect)
  const [couriers, setCouriers] = useState([]);
  const [disabledCouriers, setDisabledCouriers] = useState([]);
  const [couriersLoading, setCouriersLoading] = useState(false);

  // Test calculator
  const [testPostcode, setTestPostcode] = useState('2000');
  const [testSuburb, setTestSuburb] = useState('SYDNEY');
  const [testState, setTestState] = useState('NSW');
  const [testQuotes, setTestQuotes] = useState([]);
  const [calculating, setCalculating] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchTdCredentials();
    fetchApCredentials();
    fetchIpCredentials();
    fetchCouriers();
    fetchProviders();
  }, []);

  // --- Transdirect ---
  const fetchTdCredentials = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/transdirect`, authHeaders);
      setTdApiKey(res.data.api_key || '');
      setTdHasKey(res.data.has_key);
    } catch (err) {
      console.error('Failed to fetch TD credentials', err);
    } finally {
      setTdLoading(false);
    }
  };

  const saveTdCredentials = async () => {
    setTdSaving(true);
    try {
      await axios.put(`${API}/admin/settings/transdirect`, { api_key: tdApiKey }, authHeaders);
      toast.success('Transdirect API key saved');
      fetchTdCredentials();
    } catch { toast.error('Failed to save credentials'); }
    finally { setTdSaving(false); }
  };

  const testTdConnection = async () => {
    setTdTesting(true);
    setTdTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/settings/transdirect/test`, {}, authHeaders);
      setTdTestResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch { toast.error('Connection test failed'); }
    finally { setTdTesting(false); }
  };

  // --- AusPost ---
  const fetchApCredentials = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/auspost`, authHeaders);
      setApApiKey(res.data.api_key || '');
      setApHasKey(res.data.has_key);
    } catch (err) {
      console.error('Failed to fetch AP credentials', err);
    } finally {
      setApLoading(false);
    }
  };

  const saveApCredentials = async () => {
    setApSaving(true);
    try {
      await axios.put(`${API}/admin/settings/auspost`, { api_key: apApiKey }, authHeaders);
      toast.success('Australia Post API key saved');
      fetchApCredentials();
    } catch { toast.error('Failed to save credentials'); }
    finally { setApSaving(false); }
  };

  const testApConnection = async () => {
    setApTesting(true);
    setApTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/settings/auspost/test`, {}, authHeaders);
      setApTestResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch { toast.error('Connection test failed'); }
    finally { setApTesting(false); }
  };

  // --- Interparcel ---
  const fetchIpCredentials = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/interparcel`, authHeaders);
      setIpApiKey(res.data.api_key || '');
      setIpHasKey(res.data.has_key);
    } catch (err) {
      console.error('Failed to fetch IP credentials', err);
    } finally {
      setIpLoading(false);
    }
  };

  const saveIpCredentials = async () => {
    setIpSaving(true);
    try {
      await axios.put(`${API}/admin/settings/interparcel`, { api_key: ipApiKey }, authHeaders);
      toast.success('Interparcel API key saved');
      fetchIpCredentials();
    } catch { toast.error('Failed to save credentials'); }
    finally { setIpSaving(false); }
  };

  const testIpConnection = async () => {
    setIpTesting(true);
    setIpTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/settings/interparcel/test`, {}, authHeaders);
      setIpTestResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch { toast.error('Connection test failed'); }
    finally { setIpTesting(false); }
  };

  // --- Provider toggles ---
  const fetchProviders = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/shipping-providers`, authHeaders);
      setEnabledProviders(res.data.enabled || ['transdirect', 'auspost']);
    } catch { /* ignore */ }
  };

  const toggleProvider = async (provider) => {
    let updated;
    if (enabledProviders.includes(provider)) {
      updated = enabledProviders.filter(p => p !== provider);
    } else {
      updated = [...enabledProviders, provider];
    }
    setEnabledProviders(updated);
    try {
      await axios.put(`${API}/admin/settings/shipping-providers`, { enabled: updated }, authHeaders);
      toast.success(`${provider === 'transdirect' ? 'Transdirect' : 'Australia Post'} ${updated.includes(provider) ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update provider settings');
      fetchProviders();
    }
  };

  // --- Courier management ---
  const fetchCouriers = async () => {
    setCouriersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/shipping/couriers`, authHeaders);
      setCouriers(res.data.couriers || []);
      setDisabledCouriers(res.data.disabled_couriers || []);
    } catch { console.error('Failed to fetch couriers'); }
    finally { setCouriersLoading(false); }
  };

  const toggleCourier = async (courierKey) => {
    let updated;
    if (disabledCouriers.includes(courierKey)) {
      updated = disabledCouriers.filter(c => c !== courierKey);
    } else {
      updated = [...disabledCouriers, courierKey];
    }
    setDisabledCouriers(updated);
    try {
      await axios.put(`${API}/admin/shipping/couriers`, { disabled_couriers: updated }, authHeaders);
      toast.success(`Courier ${disabledCouriers.includes(courierKey) ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update courier settings');
      fetchCouriers();
    }
  };

  // --- Test quotes ---
  const calculateTestQuotes = async () => {
    setCalculating(true);
    setTestQuotes([]);
    try {
      const res = await axios.post(`${API}/shipping/calculate`, {
        postcode: testPostcode, suburb: testSuburb, state: testState,
        items: [{ product_id: null, quantity: 1 }],
      });
      setTestQuotes(res.data.methods || []);
      if (res.data.methods?.length) toast.success(`Got ${res.data.methods.length} shipping options`);
    } catch { toast.error('Failed to calculate quotes'); }
    finally { setCalculating(false); }
  };

  const courierList = (() => {
    if (typeof couriers === 'object' && !Array.isArray(couriers)) {
      const result = [];
      for (const [displayName, value] of Object.entries(couriers)) {
        if (typeof value === 'string') {
          result.push({ key: value, name: displayName });
        } else if (typeof value === 'object') {
          for (const [subName, slug] of Object.entries(value)) {
            result.push({ key: slug, name: `${displayName} — ${subName}` });
          }
        }
      }
      return result;
    }
    return [];
  })();

  const getProviderBadge = (provider) => {
    if (provider === 'transdirect') return <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">Transdirect</Badge>;
    if (provider === 'auspost') return <Badge className="bg-red-500/20 text-red-400 text-[10px]">AusPost</Badge>;
    if (provider === 'interparcel') return <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">Interparcel</Badge>;
    if (provider === 'local') return <Badge className="bg-zinc-500/20 text-zinc-400 text-[10px]">Local</Badge>;
    return <Badge variant="outline" className="text-[10px]">Fallback</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="shipping-settings-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Truck className="w-8 h-8 text-[#78BE20]" />
              Shipping Settings
            </h1>
            <p className="text-muted-foreground">Multi-provider shipping quotes — Transdirect + Australia Post + Interparcel</p>
          </div>
        </div>

        {/* Provider Toggles */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="provider-toggles-card">
          <CardHeader>
            <CardTitle className="text-lg">Shipping Providers</CardTitle>
            <CardDescription>Enable or disable shipping quote providers for customer checkout</CardDescription>
          </CardHeader>
          <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`flex items-center justify-between p-4 rounded-lg border ${enabledProviders.includes('transdirect') ? 'bg-blue-500/5 border-blue-500/20' : 'bg-zinc-800/30 border-zinc-800 opacity-60'}`}>
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="font-medium text-sm">Transdirect</p>
                    <p className="text-xs text-muted-foreground">Multi-courier aggregator</p>
                  </div>
                </div>
                <Switch checked={enabledProviders.includes('transdirect')} onCheckedChange={() => toggleProvider('transdirect')} data-testid="toggle-provider-transdirect" />
              </div>
              <div className={`flex items-center justify-between p-4 rounded-lg border ${enabledProviders.includes('auspost') ? 'bg-red-500/5 border-red-500/20' : 'bg-zinc-800/30 border-zinc-800 opacity-60'}`}>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="font-medium text-sm">Australia Post</p>
                    <p className="text-xs text-muted-foreground">Parcel Post + Express Post</p>
                  </div>
                </div>
                <Switch checked={enabledProviders.includes('auspost')} onCheckedChange={() => toggleProvider('auspost')} data-testid="toggle-provider-auspost" />
              </div>
              <div className={`flex items-center justify-between p-4 rounded-lg border ${enabledProviders.includes('interparcel') ? 'bg-purple-500/5 border-purple-500/20' : 'bg-zinc-800/30 border-zinc-800 opacity-60'}`}>
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="font-medium text-sm">Interparcel</p>
                    <p className="text-xs text-muted-foreground">Multi-carrier + labels + tracking</p>
                  </div>
                </div>
                <Switch checked={enabledProviders.includes('interparcel')} onCheckedChange={() => toggleProvider('interparcel')} data-testid="toggle-provider-interparcel" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transdirect API Key Card */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="transdirect-credentials-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20"><Key className="w-5 h-5 text-blue-400" /></div>
              <div>
                <CardTitle className="text-lg">Transdirect API</CardTitle>
                <CardDescription>Multi-courier shipping aggregator</CardDescription>
              </div>
              {tdHasKey && <Badge className="ml-auto bg-green-500/20 text-green-400">Connected</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {tdLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading...</div>
            ) : (
              <>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Input type={tdShowKey ? 'text' : 'password'} value={tdApiKey} onChange={(e) => setTdApiKey(e.target.value)} placeholder="Enter Transdirect API key" className="bg-zinc-800 border-zinc-700 pr-10" data-testid="transdirect-api-key" />
                    <button type="button" onClick={() => setTdShowKey(!tdShowKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                      {tdShowKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button onClick={saveTdCredentials} disabled={tdSaving} className="bg-[#78BE20] hover:bg-[#6aab1c]" data-testid="save-transdirect-key">
                    {tdSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
                  </Button>
                  <Button variant="outline" onClick={testTdConnection} disabled={tdTesting} data-testid="test-transdirect-connection">
                    {tdTesting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />} Test
                  </Button>
                </div>
                {tdTestResult && (
                  <div className={`p-3 rounded-lg text-sm ${tdTestResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {tdTestResult.message}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Get your key from <a href="https://www.transdirect.com.au/members_new/api/apimodules" target="_blank" rel="noopener noreferrer" className="text-[#78BE20] hover:underline">Transdirect API Settings <ExternalLink className="w-3 h-3 inline" /></a>
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Australia Post API Key Card */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="auspost-credentials-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20"><Mail className="w-5 h-5 text-red-400" /></div>
              <div>
                <CardTitle className="text-lg">Australia Post API</CardTitle>
                <CardDescription>Parcel Post & Express Post rates via PAC API</CardDescription>
              </div>
              {apHasKey && <Badge className="ml-auto bg-green-500/20 text-green-400">Connected</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {apLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading...</div>
            ) : (
              <>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Input type={apShowKey ? 'text' : 'password'} value={apApiKey} onChange={(e) => setApApiKey(e.target.value)} placeholder="Enter Australia Post PAC API key" className="bg-zinc-800 border-zinc-700 pr-10" data-testid="auspost-api-key" />
                    <button type="button" onClick={() => setApShowKey(!apShowKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                      {apShowKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button onClick={saveApCredentials} disabled={apSaving} className="bg-[#78BE20] hover:bg-[#6aab1c]" data-testid="save-auspost-key">
                    {apSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
                  </Button>
                  <Button variant="outline" onClick={testApConnection} disabled={apTesting} data-testid="test-auspost-connection">
                    {apTesting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />} Test
                  </Button>
                </div>
                {apTestResult && (
                  <div className={`p-3 rounded-lg text-sm ${apTestResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {apTestResult.message}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Get your key from <a href="https://developers.auspost.com.au/apis/pac" target="_blank" rel="noopener noreferrer" className="text-[#78BE20] hover:underline">AusPost Developer Centre <ExternalLink className="w-3 h-3 inline" /></a>
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Interparcel API Key Card */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="interparcel-credentials-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20"><Package className="w-5 h-5 text-purple-400" /></div>
              <div>
                <CardTitle className="text-lg">Interparcel API</CardTitle>
                <CardDescription>Multi-carrier aggregator with label generation & tracking</CardDescription>
              </div>
              {ipHasKey ? <Badge className="ml-auto bg-green-500/20 text-green-400">Connected</Badge> : <Badge className="ml-auto bg-amber-500/20 text-amber-400">Awaiting API Key</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {ipLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading...</div>
            ) : (
              <>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Input type={ipShowKey ? 'text' : 'password'} value={ipApiKey} onChange={(e) => setIpApiKey(e.target.value)} placeholder="Enter Interparcel API key" className="bg-zinc-800 border-zinc-700 pr-10" data-testid="interparcel-api-key" />
                    <button type="button" onClick={() => setIpShowKey(!ipShowKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                      {ipShowKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button onClick={saveIpCredentials} disabled={ipSaving} className="bg-[#78BE20] hover:bg-[#6aab1c]" data-testid="save-interparcel-key">
                    {ipSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
                  </Button>
                  <Button variant="outline" onClick={testIpConnection} disabled={ipTesting || !ipHasKey} data-testid="test-interparcel-connection">
                    {ipTesting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />} Test
                  </Button>
                </div>
                {ipTestResult && (
                  <div className={`p-3 rounded-lg text-sm ${ipTestResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {ipTestResult.message}
                  </div>
                )}
                {!ipHasKey && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-400">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    Requires domain migration before API key can be issued. Once ready, generate your key from the <a href="https://au.interparcel.com/account" target="_blank" rel="noopener noreferrer" className="text-[#78BE20] hover:underline">Interparcel Dashboard <ExternalLink className="w-3 h-3 inline" /></a>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Features: Multi-carrier quotes, shipment booking, label generation (emailed), tracking. <a href="https://au.interparcel.com/docs/api" target="_blank" rel="noopener noreferrer" className="text-[#78BE20] hover:underline">API Docs <ExternalLink className="w-3 h-3 inline" /></a>
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Transdirect Courier Management */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="courier-management-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#78BE20]/20"><Package className="w-5 h-5 text-[#78BE20]" /></div>
              <div>
                <CardTitle className="text-lg">Transdirect Courier Management</CardTitle>
                <CardDescription>Enable or disable specific Transdirect couriers from checkout quotes</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {couriersLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading couriers...</div>
            ) : courierList.length === 0 ? (
              <div className="text-center py-6">
                <AlertCircle className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">No couriers loaded. Test the Transdirect connection first to populate couriers.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {courierList.map((c) => {
                  const isDisabled = disabledCouriers.includes(c.key);
                  return (
                    <div key={c.key} className={`flex items-center justify-between p-3 rounded-lg border ${isDisabled ? 'bg-zinc-800/30 border-zinc-800 opacity-60' : 'bg-zinc-800/50 border-zinc-700'}`} data-testid={`courier-${c.key}`}>
                      <div className="flex items-center gap-2">
                        {isDisabled ? <Ban className="w-4 h-4 text-red-400" /> : <Truck className="w-4 h-4 text-[#78BE20]" />}
                        <span className={`text-sm font-medium capitalize ${isDisabled ? 'text-zinc-500 line-through' : 'text-white'}`}>{c.name}</span>
                      </div>
                      <Switch checked={!isDisabled} onCheckedChange={() => toggleCourier(c.key)} data-testid={`toggle-courier-${c.key}`} />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Quotes */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="test-quotes-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#78BE20]/20"><MapPin className="w-5 h-5 text-[#78BE20]" /></div>
              <div>
                <CardTitle className="text-lg">Test Shipping Quotes</CardTitle>
                <CardDescription>See what customers would see at checkout (all providers, shipping from Forbes, NSW 2871)</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Postcode</Label>
                <Input value={testPostcode} onChange={(e) => setTestPostcode(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="test-postcode" />
              </div>
              <div>
                <Label className="text-xs">Suburb</Label>
                <Input value={testSuburb} onChange={(e) => setTestSuburb(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="test-suburb" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={testState} onChange={(e) => setTestState(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="test-state" />
              </div>
              <div>
                <Label className="text-xs">&nbsp;</Label>
                <Button onClick={calculateTestQuotes} disabled={calculating} className="mt-1 w-full bg-[#78BE20] hover:bg-[#6aab1c]" data-testid="calculate-test-quotes">
                  {calculating ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <MapPin className="w-4 h-4 mr-1" />} Get Quotes
                </Button>
              </div>
            </div>

            {testQuotes.length > 0 && (
              <div className="border border-zinc-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Courier / Service</th>
                      <th className="text-left p-3 text-sm font-medium">Transit Time</th>
                      <th className="text-right p-3 text-sm font-medium">Cost</th>
                      <th className="text-center p-3 text-sm font-medium">Provider</th>
                      <th className="text-center p-3 text-sm font-medium">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testQuotes.map((q, i) => (
                      <tr key={i} className="border-t border-zinc-800">
                        <td className="p-3">
                          <div className="font-medium text-sm">{q.method}</div>
                          {q.carrier && <div className="text-xs text-muted-foreground">{q.carrier}</div>}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{q.days}</td>
                        <td className="p-3 text-right font-mono font-bold text-[#78BE20]">${q.cost?.toFixed(2)}</td>
                        <td className="p-3 text-center">{getProviderBadge(q.provider)}</td>
                        <td className="p-3 text-center">
                          <Badge variant="outline" className={q.is_live_rate ? 'border-green-500 text-green-400' : 'border-yellow-500 text-yellow-400'}>
                            {q.is_live_rate ? 'Live' : 'Estimate'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
